import { useState, useEffect, useMemo } from "react";
import {
  BsTrash3,
  BsFilter,
  BsSearch,
  BsSortDown,
  BsSortUp,
  BsEye,
  BsPlus,
  BsChevronLeft,
  BsChevronRight,
  BsChevronDoubleLeft,
  BsChevronDoubleRight,
} from "react-icons/bs";
import { MdOutlineMoreHoriz, MdOutlineEditNote } from "react-icons/md";
import { useSelector } from "react-redux";
import {
  deleteSafeJobAnalysis,
  getSafeJobAnalysisByUserId,
} from "../../../../Services/safeJobAnalysisService";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { t } from "../../../../utils/translate";
import DeleteConfirmationModal from "../../projects/components/DeleteConfirmationModal";

export default function SafeJobAnalysisTable() {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [closedData, setClosedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "updatedAt",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    dateRange: { start: "", end: "" },
  });
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [sjaToDelete, setSjaToDelete] = useState(null);

  const filteredData = useMemo(() => {
    const getNestedProperty = (obj, path) => {
      const parts = path.split(".");
      return parts.reduce(
        (acc, part) => (acc && acc[part] !== undefined ? acc[part] : ""),
        obj
      );
    };

    return data
      .filter((item) => {
        const matchesSearch =
          searchTerm === "" ||
          item.projectDetail?.safeJobAnalysisName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.projectDetail?.workDescription
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.projectId?.projectName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          item.projectId?.projectNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (item.comments &&
            item.comments.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus =
          filters.status === "all" || item.status === filters.status;

        let matchesDateRange = true;
        if (filters.dateRange.start && filters.dateRange.end) {
          const itemDate = new Date(item.updatedAt);
          const startDate = new Date(filters.dateRange.start);
          const endDate = new Date(filters.dateRange.end);
          endDate.setHours(23, 59, 59, 999); // Include the whole day
          matchesDateRange = itemDate >= startDate && itemDate <= endDate;
        }

        return matchesSearch && matchesStatus && matchesDateRange;
      })
      .sort((a, b) => {
        // Special case: updatedAt (date)
        if (sortConfig.key === "updatedAt") {
          return sortConfig.direction === "asc"
            ? new Date(a.updatedAt) - new Date(b.updatedAt)
            : new Date(b.updatedAt) - new Date(a.updatedAt);
        }

        // Special case: projectName inside projectId
        if (sortConfig.key === "projectName") {
          const aValue = a.projectId?.projectName?.toLowerCase() || "";
          const bValue = b.projectId?.projectName?.toLowerCase() || "";
          return sortConfig.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // General case
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);

        if (typeof aValue === "string" && typeof bValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
  }, [data, searchTerm, sortConfig, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentItems = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const fetchSafeJobAnalysisData = async () => {
    try {
      setLoading(true);
      const responseData = await getSafeJobAnalysisByUserId(adminId);
      const sjaData =
        responseData?.data?.filter((item) => !item?.isDeleted) || [];
      setActiveData(
        sjaData.filter((item) => item.status === "active" && !item?.isDeleted)
      );
      setClosedData(
        sjaData.filter((item) => item.status === "closed" && !item?.isDeleted)
      );
      setData(sjaData);
    } catch {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSafeJobAnalysisData();
  }, []);

  const formatDateTime = (dateTimeStr) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString("en-US", options);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelect = (id, value) => {
    setData(
      data.map((item) =>
        item._id === id ? { ...item, isSelected: value } : item
      )
    );
  };

  const handleToggleDropdown = (id, event) => {
    event.stopPropagation();
    if (currentId === id && dropdownVisible) {
      setDropdownVisible(false);
    } else {
      setCurrentId(id);
      setDropdownVisible(true);
    }
  };

  const handleDelete = async (id) => {
    const response = await deleteSafeJobAnalysis(id);
    if (response.status === "success") {
      fetchSafeJobAnalysisData();
      toast.success(t("deletedSuccessfully"));
    } else {
      toast.error(t("thereIsSomeError"));
    }
  };

  const goToPage = (page) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setFilters({
      status: "all",
      dateRange: { start: "", end: "" },
    });
    setSearchTerm("");
    setFilterMenuOpen(false);
  };

  const confirmDelete = (sja) => {
    setSjaToDelete(sja);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sjaToDelete) {
      handleDelete(sjaToDelete._id);
      setModalOpen(false);
      setSjaToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setModalOpen(false);
    setSjaToDelete(null);
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-gray-100 py-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Safe Job Analysis
              </h1>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4">
              <Link to={"/admin/sja/create-page"}>
                <button
                  type="button"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0072BB] hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <BsPlus className="mr-2 h-5 w-5" />
                  Create Safe Job Analysis
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and filter bar */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <BsSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search by name, description or comments"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => setFilterMenuOpen(!filterMenuOpen)}
              >
                <BsFilter className="mr-2 h-5 w-5" />
                Filter
              </button>

              {filterMenuOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div
                    className="py-1 p-4"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        value={filters.status}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                      >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Range
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="date"
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={filters.dateRange.start}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              dateRange: {
                                ...filters.dateRange,
                                start: e.target.value,
                              },
                            })
                          }
                        />
                        <input
                          type="date"
                          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={filters.dateRange.end}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              dateRange: {
                                ...filters.dateRange,
                                end: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={resetFilters}
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => setFilterMenuOpen(false)}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <select
              className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <svg
                className="h-16 w-16 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 20a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No results found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter to find what you're looking
                for.
              </p>
              <button
                onClick={resetFilters}
                className="mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setData(
                            data.map((item) => ({
                              ...item,
                              isSelected: isChecked,
                            }))
                          );
                        }}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("projectName")}
                  >
                    <div className="flex items-center">
                      Project Name
                      {sortConfig.key === "projectName" &&
                        (sortConfig.direction === "asc" ? (
                          <BsSortUp className="ml-1 h-4 w-4" />
                        ) : (
                          <BsSortDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("safeJobAnalysisName")}
                  >
                    <div className="flex items-center">
                      Analysis Name
                      {sortConfig.key === "safeJobAnalysisName" &&
                        (sortConfig.direction === "asc" ? (
                          <BsSortUp className="ml-1 h-4 w-4" />
                        ) : (
                          <BsSortDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => requestSort("updatedAt")}
                  >
                    <div className="flex items-center">
                      Last Edit
                      {sortConfig.key === "updatedAt" &&
                        (sortConfig.direction === "asc" ? (
                          <BsSortUp className="ml-1 h-4 w-4" />
                        ) : (
                          <BsSortDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={item.isSelected || false}
                        onChange={(e) =>
                          handleSelect(item._id, e.target.checked)
                        }
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <div className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                          {item.projectId?.projectName || "Unnamed Project"}
                        </div>
                        <div className="text-sm text-gray-500">
                          Project No:{" "}
                          <span className="font-medium text-gray-900">
                            {item.projectId?.projectNumber || "N/A"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {item.projectDetail.safeJobAnalysisName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {item.projectDetail.workDescription}
                      </div>
                      {item.comments && !item.isInputOpen && (
                        <div className="mt-2 text-xs text-gray-500 italic max-w-xs">
                          <span className="font-medium">Comment:</span>{" "}
                          {item.comments}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(item.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          item.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {item.status === "active" ? "Active" : "Closed"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2 relative">
                        {item.isSelected ? (
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => confirmDelete(item)}
                          >
                            <BsTrash3 className="h-5 w-5" />
                          </button>
                        ) : (
                          <>
                            <button
                              className="text-gray-500 hover:text-gray-700"
                              onClick={(e) => handleToggleDropdown(item._id, e)}
                            >
                              <MdOutlineMoreHoriz className="h-5 w-5" />
                            </button>

                            {dropdownVisible && currentId === item._id && (
                              <div className="absolute right-0 top-0 mt-8 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                <div
                                  className="py-1"
                                  role="menu"
                                  aria-orientation="vertical"
                                >
                                  <Link
                                    to={`/admin/sja/detail-page/${item._id}`}
                                  >
                                    <button
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      role="menuitem"
                                    >
                                      <BsEye className="inline-block mr-2 h-4 w-4" />
                                      View
                                    </button>
                                  </Link>
                                  <Link
                                    to={`/admin/sja/edit-page/${item?.projectId?._id}/${item?._id}`}
                                  >
                                    <button
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      role="menuitem"
                                    >
                                      <MdOutlineEditNote className="inline-block mr-2 h-4 w-4" />
                                      Edit
                                    </button>
                                  </Link>

                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-gray-100"
                                    role="menuitem"
                                    onClick={() => confirmDelete(item)}
                                  >
                                    <BsTrash3 className="inline-block mr-2 h-4 w-4" />
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {filteredData.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(
                        currentPage * itemsPerPage,
                        filteredData.length
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium">{filteredData.length}</span>{" "}
                    results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">First</span>
                      <BsChevronDoubleLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <BsChevronLeft className="h-5 w-5" />
                    </button>

                    {/* Page numbers */}
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNumber = i + 1;
                      // Show current page, and 1 page before and after
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => goToPage(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }

                      // Show ellipsis
                      if (
                        (pageNumber === 2 && currentPage > 3) ||
                        (pageNumber === totalPages - 1 &&
                          currentPage < totalPages - 2)
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }

                      return null;
                    })}

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <BsChevronRight className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <span className="sr-only">Last</span>
                      <BsChevronDoubleRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                  <svg
                    className="h-6 w-6 text-green-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active SJA
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {activeData.length}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-red-100 rounded-md p-3">
                  <svg
                    className="h-6 w-6 text-red-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Closed SJA
                    </dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">
                        {closedData.length}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={modalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        projectName={sjaToDelete?.projectDetail?.safeJobAnalysisName}
      />
    </div>
  );
}
